/**
 * Honeycomb of six hexagons around the Kore mark. Clicking one swaps the panel
 * beside it. Shared by the About values section and the Infrastructure page.
 *
 * Layout is done in script rather than CSS because a honeycomb needs each cell
 * offset by a fraction of its neighbour's width, which no grid gives you.
 */

const roundedHexPath = (width, height, radius, inset = 0) => {
    const w = width - inset * 2;
    const h = height - inset * 2;
    const r = Math.min(radius, w * 0.15);
    const cx = width / 2;

    const points = [
        { x: cx, y: inset },
        { x: width - inset, y: h * 0.25 + inset },
        { x: width - inset, y: h * 0.75 + inset },
        { x: cx, y: height - inset },
        { x: inset, y: h * 0.75 + inset },
        { x: inset, y: h * 0.25 + inset },
    ];

    let path = '';
    for (let i = 0; i < 6; i++) {
        const curr = points[i];
        const next = points[(i + 1) % 6];
        const prev = points[(i + 5) % 6];

        const unit = (from, to) => {
            const dx = to.x - from.x;
            const dy = to.y - from.y;
            const len = Math.sqrt(dx * dx + dy * dy);
            return { x: dx / len, y: dy / len };
        };

        const a = unit(curr, prev);
        const b = unit(curr, next);
        const p1 = { x: curr.x + a.x * r, y: curr.y + a.y * r };
        const p2 = { x: curr.x + b.x * r, y: curr.y + b.y * r };

        path += `${i === 0 ? 'M' : 'L'} ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} `;
        path += `Q ${curr.x.toFixed(2)} ${curr.y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)} `;
    }
    return `${path}Z`;
};

export const hexagonGrid = (rootSelector) => {
    const root = document.querySelector(rootSelector);
    if (!root) return;

    const grid = root.querySelector('.hexagon-grid');
    const hexagons = [...root.querySelectorAll('.hexagon')];
    if (!grid || !hexagons.length) return;

    const layout = () => {
        const gridWidth = grid.offsetWidth;
        if (!gridWidth) return;

        const gap = gridWidth * 0.025;
        const hexWidth = (gridWidth - gap * 2) / 2.5;
        const hexHeight = hexWidth * 1.15;

        grid.style.setProperty('--hex-width', `${hexWidth}px`);
        grid.style.setProperty('--hex-height', `${hexHeight}px`);

        const border = roundedHexPath(100, 115, 12, 2);
        const background = roundedHexPath(100, 115, 10, 4);
        root.querySelectorAll('.hex-path-border').forEach((el) => el.setAttribute('d', border));
        root.querySelectorAll('.hex-path-bg').forEach((el) => el.setAttribute('d', background));

        const offsetX = (gridWidth - (hexWidth * 3 + gap * 2)) / 2;
        const rowOffset = hexWidth * 0.5 + gap * 0.5;
        const verticalGap = hexHeight * 0.78;

        hexagons.forEach((hex) => {
            const row = parseInt(hex.dataset.row, 10);
            const col = parseInt(hex.dataset.col, 10);
            const indent = row === 1 ? 0 : rowOffset;
            hex.style.left = `${offsetX + indent + col * (hexWidth + gap)}px`;
            hex.style.top = `${verticalGap * row}px`;
        });

        grid.style.height = `${verticalGap * 2 + hexHeight}px`;
    };

    layout();

    let pending;
    window.addEventListener('resize', () => {
        clearTimeout(pending);
        pending = setTimeout(layout, 100);
    });

    // the panel cross-fades, so ignore clicks until it has settled
    const fade = 250;
    let busy = false;

    root.querySelectorAll('.hexagon[data-value]').forEach((hex) => {
        hex.addEventListener('click', function () {
            if (busy || this.classList.contains('active')) return;

            const target = root.querySelector(`.hex-content-item[data-value="${this.dataset.value}"]`);
            const current = root.querySelector('.hex-content-item.active');
            if (!target) return;

            busy = true;
            hexagons.forEach((h) => h.classList.remove('active'));
            this.classList.add('active');

            if (current) current.classList.remove('active');
            setTimeout(() => {
                target.classList.add('active');
                setTimeout(() => { busy = false; }, fade);
            }, current ? fade : 0);
        });
    });
};
